import debug from 'debug'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import {
  type AppRouteHandle,
  useLocation,
  useMatches,
} from 'src/utils/router/route'
import { trackPageRender } from 'src/utils/tracker'
import { claimPathname, releasePathname } from './pageRenderClaim'

const log = debug('pixai:render-tracker')

export interface RenderTrackerOptions {
  /** Amplitude page name, e.g. 'home', 'artwork-detail'. Used as the `page` property in the event. */
  pageName: string

  /**
   * Optional readiness gate for async-data pages.
   * - If omitted or `undefined`: this is a no-op — the auto-tracker (RenderTracker)
   *   handles it at the layout level.
   * - If provided: the hook "claims" the current pathname (preventing the auto-tracker
   *   from firing) and waits until `ready === true` before starting the rAF check.
   */
  ready?: boolean

  /**
   * Any extra properties to merge into the Amplitude event.
   * Must be memoized (useMemo / stable reference) to avoid re-triggering
   * the tracking effect on every render.
   */
  extraProps?: Record<string, unknown>
}

/**
 * Tracks page render timing by firing a `page_render` event.
 *
 * **Two modes of operation:**
 *
 * 1. **Auto mode** (no `ready` param): Do not call this hook — the `RenderTracker`
 *    component in Root.tsx handles tracking automatically after mount + rAF.
 *
 * 2. **Async mode** (with `ready` param): For pages that load async data before
 *    being "ready". The hook claims the pathname to suppress auto-tracking,
 *    then fires `page_render` when `ready` becomes `true` + rAF.
 *
 * The `requestAnimationFrame` step is mandatory — it ensures the browser has
 * completed at least one paint before we consider the page "rendered".
 *
 * Fires **exactly once** per mount. A `ref` flag guards against re-firing
 * if `ready` toggles or the component re-renders.
 *
 * @example
 * // In an async page component:
 * const { data, loading } = useGetArtwork(id)
 * useRenderTracker({
 *   pageName: 'artwork-detail',
 *   ready: !loading && !!data,
 * })
 */
export function useRenderTracker({
  pageName,
  ready,
  extraProps,
}: RenderTrackerOptions): void {
  const location = useLocation()
  const matches = useMatches<unknown, AppRouteHandle>()
  const params = useParams()
  const pathname = location.pathname
  const firedForRef = useRef<string | null>(null)

  // When `ready` is provided (async mode), claim the pathname to prevent
  // the auto-tracker from firing. This effect runs before RenderTracker's
  // effect due to React's sibling effect ordering.
  //
  // Uses useLocation() (locale-stripped) so the claimed pathname matches
  // what RenderTracker checks via isPathnameClaimed().
  useEffect(() => {
    if (ready === undefined) return

    log('claiming pathname %s for page %s (async mode)', pathname, pageName)
    claimPathname(pathname)

    return () => {
      log('releasing claim for pathname %s', pathname)
      releasePathname(pathname)
    }

    // is intentional: we only care about the transition between "no ready param"
    // and "has ready param", not the boolean value of ready itself.
  }, [ready !== undefined, pathname, pageName])

  // Fire page_render when ready (async mode only).
  useEffect(() => {
    if (ready === undefined) return
    if (!ready) return
    if (firedForRef.current === pathname) return

    const rafId = requestAnimationFrame(() => {
      if (firedForRef.current === pathname) return
      firedForRef.current = pathname
      log(
        'firing page_render for page %s (async mode) %o',
        pageName,
        extraProps,
      )
      trackPageRender({ location, matches, params, pageName, extraProps })
    })

    return () => cancelAnimationFrame(rafId)

    // and params always change together with pathname, which is the true trigger.
  }, [pathname, pageName, ready, extraProps])
}
